import type { ActivityHeatmapData } from '@/core/types';
import { cn } from '@/lib/utils';

type Props = { data: ActivityHeatmapData };

/**
 * GitHub-style activity heatmap.
 * Layout: rows = weekdays Mon–Sun, columns = weeks (oldest left → newest right).
 * Cell intensity = number of completed sessions on that day.
 */
export function ActivityHeatmap({ data }: Props) {
  // The API returns days from start_date (Mon..Sun by sequence) so we tile by row.
  // Build a 7-row × N-col matrix.
  const { days } = data;
  const weeks = Math.ceil(days.length / 7);
  const matrix: ({ date: string; sessions: number } | null)[][] = Array.from({ length: 7 }, () =>
    Array(weeks).fill(null),
  );

  // Determine the first day's weekday index (Mon=0..Sun=6).
  const firstDate = new Date(data.start_date + 'T00:00:00Z');
  const firstDay = firstDate.getUTCDay(); // 0=Sun..6=Sat
  const startRow = firstDay === 0 ? 6 : firstDay - 1; // Mon-aligned

  for (let i = 0; i < days.length; i++) {
    const row = (startRow + i) % 7;
    const col = Math.floor((startRow + i) / 7);
    if (col < weeks) matrix[row][col] = days[i];
  }

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
        Activity · last {data.weeks} weeks
      </h3>
      <div className="overflow-x-auto">
        <div
          className="inline-grid grid-flow-col gap-0.5"
          style={{ gridTemplateRows: 'repeat(7, 1fr)' }}
        >
          {matrix.flatMap((row, rowIdx) =>
            row.map((cell, colIdx) => <Cell key={`${rowIdx}-${colIdx}`} cell={cell} />),
          )}
        </div>
      </div>
      <Legend />
    </section>
  );
}

function Cell({ cell }: { cell: { date: string; sessions: number } | null }) {
  if (!cell) return <div className="size-3 rounded-sm bg-transparent" aria-hidden="true" />;
  return (
    <div
      className={cn('size-3 rounded-sm', intensityClass(cell.sessions))}
      title={`${cell.date} · ${cell.sessions} session${cell.sessions === 1 ? '' : 's'}`}
    />
  );
}

function intensityClass(sessions: number): string {
  if (sessions === 0) return 'bg-border';
  if (sessions === 1) return 'bg-primary/30';
  if (sessions === 2) return 'bg-primary/55';
  if (sessions === 3) return 'bg-primary/80';
  return 'bg-primary';
}

function Legend() {
  return (
    <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
      <span>less</span>
      <div className="bg-border size-3 rounded-sm" />
      <div className="bg-primary/30 size-3 rounded-sm" />
      <div className="bg-primary/55 size-3 rounded-sm" />
      <div className="bg-primary/80 size-3 rounded-sm" />
      <div className="bg-primary size-3 rounded-sm" />
      <span>more</span>
    </div>
  );
}
